/**
 * Single source of truth for indexing policy.
 *
 * robots.txt, sitemap generation, per-route `noindex` meta and the runtime
 * consistency checker all read from this module so the three can never drift.
 */

/** Path prefixes blocked in robots.txt. Must also be noindex + out of sitemaps. */
export const ROBOTS_DISALLOW: string[] = [
  "/admin/",
  "/go/",
  "/auth",
  "/my-picks",
  "/seo-status",
  "/api/",
];

/** Exceptions that stay crawlable even though a broader Disallow covers them. */
export const ROBOTS_ALLOW_EXCEPTIONS: string[] = ["/api/public/game-logos/"];

/**
 * Crawl traps: URL shapes that can never resolve to an indexable page —
 * future-dated result URLs and query-stringed result URLs. Emitted in
 * robots.txt and honoured by `isRobotsDisallowed` so the two never drift.
 */
export function robotsCrawlTraps(baseDate = new Date()): string[] {
  const year = baseDate.getUTCFullYear();
  const safeYear = year >= 2020 && year <= 2100 ? year : 2026;
  const futureYears = Array.from({ length: 5 }, (_, i) => safeYear + 1 + i);
  return [
    ...futureYears.flatMap((y) => [`/results/${y}-`, `/*/results/${y}-`]),
    "/results/*?",
    "/*/results/*?",
  ];
}

function trapToRegExp(pattern: string): RegExp {
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");
  return new RegExp(`^${escaped}`);
}

export function isCrawlTrap(path: string, baseDate = new Date()): boolean {
  const p = normalizePath(path);
  return robotsCrawlTraps(baseDate).some((pattern) => trapToRegExp(pattern).test(p));
}

/**
 * URLs that used to exist and were deleted or replaced. They are served with
 * HTTP 410 Gone (a permanent removal signal Google honours faster than 404)
 * and are excluded from every sitemap.
 */
// Add a path here when a page is removed or replaced — never leave it 404ing.
export const GONE_PATHS: string[] = [];

/** Prefixes whose entire subtree is gone. */
export const GONE_PREFIXES: string[] = ["/legacy/"];

export const CANONICAL_ORIGIN = "https://usalotteryclub.com";

/**
 * Crawl-budget policy for dated result pages.
 *
 * The site can generate thousands of near-identical `/…/results/YYYY-MM-DD`
 * URLs. Submitting them all leaves most stuck in Search Console's
 * "Discovered – currently not indexed" bucket and starves the pages that
 * matter. So:
 *  - only draws from the last RESULT_SITEMAP_DAYS days are advertised in sitemaps;
 *  - draws older than RESULT_NOINDEX_DAYS are served `noindex, follow` — still
 *    crawlable and still passing link equity to the game/history pages, just
 *    not competing for index slots.
 * Between the two windows the page stays indexable but unsubmitted.
 */
export const RESULT_SITEMAP_DAYS = 30;
export const RESULT_NOINDEX_DAYS = 90;

const RESULT_DATE_RE = /\/results\/(\d{4}-\d{2}-\d{2})$/;

/** The draw date encoded in a dated result URL, if the path is one. */
export function resultPathDate(path: string): string | null {
  const m = pathOnly(path).match(RESULT_DATE_RE);
  return m ? m[1] : null;
}

function daysBetween(dateIso: string, baseDate: Date): number {
  const then = Date.parse(`${dateIso}T00:00:00Z`);
  if (Number.isNaN(then)) return 0;
  return Math.floor((baseDate.getTime() - then) / 86400000);
}

/** True when a dated result page is older than the sitemap freshness window. */
export function isStaleResultPath(path: string, baseDate = new Date()): boolean {
  const date = resultPathDate(path);
  return date !== null && daysBetween(date, baseDate) > RESULT_SITEMAP_DAYS;
}

/** True when a dated result page should be served `noindex, follow`. */
export function isArchivedResultPath(path: string, baseDate = new Date()): boolean {
  const date = resultPathDate(path);
  return date !== null && daysBetween(date, baseDate) > RESULT_NOINDEX_DAYS;
}

/** True when a draw date is old enough to be excluded from the sitemap. */
export function isStaleResultDate(date: string, baseDate = new Date()): boolean {
  return daysBetween(date, baseDate) > RESULT_SITEMAP_DAYS;
}

/** Site locale — single-locale (US English) site. */
export const SITE_LOCALE = "en-US";

export function normalizePath(input: string): string {
  let p = input.trim();
  if (/^https?:\/\//i.test(p)) {
    try {
      const u = new URL(p);
      p = u.pathname + u.search;
    } catch {
      /* keep as-is */
    }
  }
  const q = p.indexOf("?");
  const qs = q >= 0 ? p.slice(q) : "";
  let base = q >= 0 ? p.slice(0, q) : p;
  if (!base.startsWith("/")) base = `/${base}`;
  if (base.length > 1 && base.endsWith("/")) base = base.slice(0, -1);
  return base + qs;
}

const pathOnly = (p: string) => normalizePath(p).split("?")[0];

export function isRobotsDisallowed(path: string): boolean {
  const p = pathOnly(path);
  if (ROBOTS_ALLOW_EXCEPTIONS.some((prefix) => p.startsWith(prefix))) return false;
  if (isCrawlTrap(path)) return true;
  return ROBOTS_DISALLOW.some((rule) =>
    rule.endsWith("/") ? p.startsWith(rule) || p === rule.slice(0, -1) : p === rule || p.startsWith(`${rule}/`),
  );
}

export function isGone(path: string): boolean {
  const p = pathOnly(path);
  return GONE_PATHS.includes(p) || GONE_PREFIXES.some((prefix) => p.startsWith(prefix));
}

/** A path must be noindex when it is robots-blocked or permanently gone. */
export function shouldBeNoindex(path: string): boolean {
  return isRobotsDisallowed(path) || isGone(path);
}

/** A path may appear in a sitemap only when it is indexable. */
export function isSitemapEligible(path: string): boolean {
  return !shouldBeNoindex(path) && !isStaleResultPath(path);
}

export function canonicalUrl(path: string): string {
  // Canonicals must be self-referencing and query-free: filter params such as
  // ?ig= produce duplicate URLs that Search Console reports as non-indexable.
  const p = pathOnly(path);
  return p === "/" ? `${CANONICAL_ORIGIN}/` : `${CANONICAL_ORIGIN}${p}`;
}

export interface SeoIssue {
  path: string;
  rule: string;
  detail: string;
}

/** Check sitemap membership against robots.txt / gone rules. */
export function auditSitemapPaths(paths: string[]): SeoIssue[] {
  const issues: SeoIssue[] = [];
  const seen = new Set<string>();
  for (const raw of paths) {
    const path = normalizePath(raw);
    if (seen.has(path)) {
      issues.push({ path, rule: "duplicate-in-sitemap", detail: "URL listed more than once" });
      continue;
    }
    seen.add(path);
    if (isRobotsDisallowed(path)) {
      issues.push({ path, rule: "sitemap-vs-robots", detail: "In sitemap but blocked by robots.txt" });
    }
    if (isGone(path)) {
      issues.push({ path, rule: "sitemap-vs-gone", detail: "In sitemap but the URL is 410 Gone" });
    }
  }
  return issues;
}

export interface PageObservation {
  path: string;
  status: number;
  canonical?: string | null;
  robotsMeta?: string | null;
  xRobotsTag?: string | null;
  hreflang?: string[];
  inSitemap: boolean;
}

/** Check one fetched page's meta robots / canonical / hreflang against policy. */
export function auditPage(obs: PageObservation): SeoIssue[] {
  const issues: SeoIssue[] = [];
  const path = normalizePath(obs.path);
  const archived = isArchivedResultPath(path);
  const expectNoindex = shouldBeNoindex(path) || archived;
  const robots = `${obs.robotsMeta ?? ""} ${obs.xRobotsTag ?? ""}`.toLowerCase();
  const hasNoindex = robots.includes("noindex");

  if (isGone(path)) {
    if (obs.status !== 410) {
      issues.push({ path, rule: "gone-status", detail: `Expected HTTP 410, got ${obs.status}` });
    }
    return issues;
  }

  if (expectNoindex && !hasNoindex) {
    issues.push({ path, rule: "missing-noindex", detail: "robots.txt-blocked URL is missing a noindex directive" });
  }
  if (!expectNoindex && hasNoindex) {
    issues.push({ path, rule: "unexpected-noindex", detail: "Indexable URL declares noindex" });
  }
  if (!expectNoindex) {
    if (obs.status !== 200) {
      issues.push({ path, rule: "bad-status", detail: `Indexable URL returned ${obs.status}` });
    }
    const expected = canonicalUrl(path);
    if (!obs.canonical) {
      issues.push({ path, rule: "missing-canonical", detail: "No <link rel=canonical>" });
    } else if (normalizePath(obs.canonical) !== normalizePath(expected)) {
      issues.push({
        path,
        rule: "canonical-mismatch",
        detail: `Canonical points to ${obs.canonical}, expected ${expected}`,
      });
    }
    if (obs.hreflang && obs.hreflang.length > 0) {
      if (!obs.hreflang.includes(SITE_LOCALE) || !obs.hreflang.includes("x-default")) {
        issues.push({
          path,
          rule: "hreflang-incomplete",
          detail: `Expected ${SITE_LOCALE} + x-default, got ${obs.hreflang.join(", ")}`,
        });
      }
    }
    if (
      !obs.inSitemap &&
      !path.startsWith("/results/") &&
      !resultPathDate(path) &&
      path !== "/routes-index"
    ) {
      issues.push({ path, rule: "missing-from-sitemap", detail: "Indexable URL is not listed in any sitemap" });
    }
  } else if (obs.canonical && normalizePath(obs.canonical) !== normalizePath(canonicalUrl(path))) {
    issues.push({ path, rule: "noindex-canonical", detail: "Non-indexable URL sets a foreign canonical" });
  }
  return issues;
}
